import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo, useState } from "react"
import { selectIsSavingChanges, removeContactFromList } from "./slice"
import * as Icons from "@nova/component-icons"
import { SavedContact, ShareContact } from "@nova/schemas"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { getColorFromId } from "@nova/util-strings"
import { WorkspaceCollaboratorProfile } from "../collaborators/WorkspaceCollaboratorProfile"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"
import { useSavedContacts } from "./SavedContactsQueries"

export function Contact({
    contact,
    contactListId,
    allowRemoval,
}: {
    contactListId: string
    allowRemoval: boolean
    contact: ShareContact
}) {
    const dispatch = useAppDispatch()
    const isSavingChanges = useAppSelector(selectIsSavingChanges)
    const [isHovering, setIsHovering] = useState(false)
    const members = useAppSelector(selectWorkspaceMembers)
    const { contacts } = useSavedContacts()
    const contactsMap = useMemo(() => {
        return contacts.reduce(
            (acc, cur) => {
                if (cur.email) {
                    acc[cur.email] = cur
                }
                if (cur.phone) {
                    acc[cur.phone] = cur
                }
                return acc
            },
            {} as Record<string, SavedContact>
        )
    }, [contacts])
    const member = useMemo(() => {
        if (contact.type === "email") {
            return members?.find((m) => m.email === contact.email)
        }
        return undefined
    }, [contact.email, contact.type, members])
    const savedContact: SavedContact | undefined = useMemo(() => {
        if (contact.type === "email") {
            return contactsMap[contact.email]
        }
        return contactsMap[contact.phoneNumber]
    }, [contact.email, contact.phoneNumber, contact.type, contactsMap])
    return (
        <div
            className="flex flex-col w-full py-2.5 relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <div className="flex items-center gap-4">
                {member ? (
                    <UserProfileModalTrigger userId={member.userId}>
                        <WorkspaceCollaboratorProfile
                            userId={member.userId}
                            fullName={member.fullName}
                            email={member.email}
                        />
                    </UserProfileModalTrigger>
                ) : contact.type === "email" ? (
                    <div
                        className="w-8 h-8 rounded-lg flex justify-center items-center"
                        style={{ backgroundColor: getColorFromId(contact.email) }}
                    >
                        <Icons.Icon Icon={Icons.NoAccount} iconClassName="text-white-400" />
                    </div>
                ) : (
                    <div className="w-8 h-8 rounded-lg flex justify-center items-center">
                        <Icons.Icon Icon={Icons.PhoneMessage} iconClassName="text-white" />
                    </div>
                )}
                <div className="flex flex-col justify-center grow">
                    {!member && savedContact && (
                        <div>
                            <h5>{savedContact.name}</h5>
                            <small className="text-gray-200 lowercase">
                                {contact.type === "email" ? contact.email : contact.phoneNumber}
                            </small>
                        </div>
                    )}
                    {!member && !savedContact && (
                        <div className="text-white">
                            {contact.type === "email" ? contact.email : contact.phoneNumber}
                        </div>
                    )}
                </div>
                {isHovering && allowRemoval && !isSavingChanges && (
                    <Icons.SmallIcon
                        Icon={Icons.PersonRemove}
                        iconClassName="text-gray-100 hover:text-red-500"
                        className="cursor-pointer"
                        onClick={async (e) => {
                            e.stopPropagation()
                            dispatch(
                                removeContactFromList({
                                    contact,
                                    contactListId,
                                })
                            )
                        }}
                    />
                )}
            </div>
        </div>
    )
}
